import { useEffect, useState } from "react";
import OrganizationService from "../Organization.service";

export const persistTranscriptString = "RedactionPersistOriginalTranscript";
export const extractPIIString = "ExtractPII";

type Props = {
    id: string;
};

/**
 * Returns redaction options
 *
 * @multiSelectRedactionOptions Options used in the muliSelect control, excludes extractPII and persistOGTranscript
 */
const useRedactionOptions = ({ id }: Props) => {
    const [redactionOptions, setRedactionOptions] = useState<string[] | null>();
    const [activeRedactionOptions, setActiveRedactionOptions] = useState<
        string[] | null
    >();
    const [extractPIIEnabled, setExtractPIIEnabled] = useState(false);
    const [persistOriginalTranscript, setPersistOriginalTranscript] =
        useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [voiceRedactionFactor, setVoiceRedactionFactor] = useState<
        number | undefined
    >();

    const multiSelectRedactionOptions = redactionOptions?.filter(
        (option) =>
            option !== extractPIIString && option !== persistTranscriptString,
    );

    useEffect(() => {
        let destroyed = false;

        const runFn = async () => {
            try {
                setIsLoading(true);
                const redactionOptions =
                    await OrganizationService.getRedactionProcessingOptions();
                const activeRedactionOptions =
                    await OrganizationService.getActiveRedactionProcessingOptions();
                const voiceRedactionFactor =
                    await OrganizationService.getVoiceRedactionFactor(id);

                if (!destroyed) {
                    setRedactionOptions(redactionOptions);
                    setVoiceRedactionFactor(voiceRedactionFactor);

                    const filteredActiveRedactionOptions =
                        activeRedactionOptions
                            .map((i) => i.redactionOption)
                            ?.filter(
                                (optString) =>
                                    optString !== extractPIIString &&
                                    optString !== persistTranscriptString,
                            );

                    const extractPIIEnabled = !!activeRedactionOptions?.find(
                        (i) => i.redactionOption === extractPIIString,
                    );

                    const persistOriginalTranscript =
                        !!activeRedactionOptions?.find(
                            (i) =>
                                i.redactionOption === persistTranscriptString,
                        );

                    setActiveRedactionOptions(filteredActiveRedactionOptions);

                    setExtractPIIEnabled(extractPIIEnabled);
                    setPersistOriginalTranscript(persistOriginalTranscript);

                    setIsLoading(false);
                }
            } catch (err) {
                console.error(err);
                setIsLoading(false);
            }
        };
        runFn();
        return () => {
            destroyed = true;
            setRedactionOptions(null);
            setActiveRedactionOptions(null);
        };
    }, [id]);

    return {
        redactionOptions,
        activeRedactionOptions,
        setActiveRedactionOptions,
        isLoading,
        multiSelectRedactionOptions,
        extractPIIEnabled,
        setExtractPIIEnabled,
        persistOriginalTranscript,
        setPersistOriginalTranscript,
        voiceRedactionFactor,
        setVoiceRedactionFactor,
    };
};

export default useRedactionOptions;
